/**
 * Site webhook ↔ camera stream links (ENG-2427 item 1).
 *
 * A site webhook (`webhook_customer`) reaches a camera through a stream link
 * (`webhook/`, a WebhookStream: stream + event types + custom fields). Only a
 * link's event types go into the connector settings; the consumer reads the
 * URL, body, auth and custom fields live. So the site needs a Sync exactly
 * when a link with event types is added or removed, or a link's event types
 * change — the same rule admin uses to mark the site undeployed.
 */

import { createApi } from "../../../../utils/createApi";
import { SiteWebhook } from "./siteWebhooks";

export interface WebhookEventTypeOption {
  id: number;
  value: string;
  name: string;
}

export interface SiteStream {
  id: number;
  camera: number;
  name: string;
  product: string;
}

/** `GET webhook/?customer_id=` row (WebhookStreamDetailSerializer). */
export interface WebhookLink {
  id: number;
  stream: SiteStream;
  webhook: SiteWebhook;
  custom_fields: Record<string, unknown> | null;
  /** Event type values, e.g. "detection". */
  event_types: string[];
}

export interface LinkPlan {
  /** Streams that get a new link. */
  add: number[];
  /** Links to delete. */
  remove: WebhookLink[];
  /** Links whose event types change. */
  patch: WebhookLink[];
  /** Whether the change reaches the connector settings. */
  needsSync: boolean;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

/**
 * What saving the setup flow does to one webhook's links.
 *
 * `eventTypes` applies to every selected stream. When `eventTypesChanged` is
 * false, kept links keep their own event types (links can differ, and
 * opening the dialog must not rewrite them); new links still get
 * `eventTypes`.
 */
export const planLinkChanges = ({
  existingLinks,
  selectedStreamIds,
  eventTypes,
  eventTypesChanged,
}: {
  existingLinks: WebhookLink[];
  selectedStreamIds: number[];
  eventTypes: string[];
  eventTypesChanged: boolean;
}): LinkPlan => {
  const linkedStreamIds = new Set(existingLinks.map((link) => link.stream.id));
  const selected = new Set(selectedStreamIds);
  const add = selectedStreamIds.filter((id) => !linkedStreamIds.has(id));
  const remove = existingLinks.filter((link) => !selected.has(link.stream.id));
  const patch = eventTypesChanged
    ? existingLinks.filter(
        (link) =>
          selected.has(link.stream.id) &&
          !sameSet(link.event_types ?? [], eventTypes)
      )
    : [];
  const needsSync =
    (add.length > 0 && eventTypes.length > 0) ||
    remove.some((link) => (link.event_types ?? []).length > 0) ||
    patch.length > 0;
  return { add, remove, patch, needsSync };
};

/** Event types shared by the links, or their union when they differ. */
export const initialEventTypes = (links: WebhookLink[]): string[] => {
  const values = new Set<string>();
  links.forEach((link) =>
    (link.event_types ?? []).forEach((value) => values.add(value))
  );
  return Array.from(values);
};

export const linksDisagree = (links: WebhookLink[]): boolean =>
  links.some(
    (link) => !sameSet(link.event_types ?? [], links[0]?.event_types ?? [])
  );

/** Deleting the site webhook cascades its links: Sync if any carried events. */
export const deleteNeedsSync = (links: WebhookLink[]): boolean =>
  links.some((link) => (link.event_types ?? []).length > 0);

export const readEventTypeOptions = (): WebhookEventTypeOption[] => {
  try {
    const options = JSON.parse(localStorage.webHookEventTypeOptions ?? "[]");
    return Array.isArray(options) ? options : [];
  } catch {
    return [];
  }
};

export const eventTypeIds = (
  values: string[],
  options: WebhookEventTypeOption[]
): number[] =>
  values
    .map((value) => options.find((option) => option.value === value)?.id)
    .filter((id): id is number => typeof id === "number");

/** Camera names from the Site About camera load (localStorage.siteCameras). */
export const readSiteCameraNames = (): Record<number, string> => {
  try {
    const stored = JSON.parse(localStorage.siteCameras ?? "null");
    const results: any[] = Array.isArray(stored?.results) ? stored.results : [];
    return Object.fromEntries(
      results.map((camera) => [camera.id, camera.camera_name])
    );
  } catch {
    return {};
  }
};

export const streamLabel = (
  stream: SiteStream,
  cameraNames: Record<number, string>
): string => {
  const camera = cameraNames[stream.camera] ?? `Camera ${stream.camera}`;
  const product = stream.product ? ` (${stream.product})` : "";
  return `${camera} · ${stream.name}${product}`;
};

const resultsOf = <T>(data: any): T[] => {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.results) ? data.results : [];
};

// --- API -----------------------------------------------------------------

const PAGE_SIZE = 1000;

export const fetchSiteWebhookLinks = async (
  siteId: number
): Promise<WebhookLink[]> => {
  const response = await createApi("").get(
    `webhook/?customer_id=${siteId}&page_size=${PAGE_SIZE}`
  );
  return resultsOf<WebhookLink>(response.data);
};

export const fetchSiteStreams = async (
  siteId: number
): Promise<SiteStream[]> => {
  const response = await createApi("").get(
    `stream/product/?customer=${siteId}&page_size=${PAGE_SIZE}`
  );
  return resultsOf<SiteStream>(response.data);
};

export const fetchSiteWebhooks = async (
  siteId: number
): Promise<SiteWebhook[]> => {
  const response = await createApi("").get(
    `webhook_customer/?customer=${siteId}`
  );
  return Array.isArray(response.data) ? response.data : [];
};

/** Apply a plan for one webhook. Stops at the first failed request. */
export const applyLinkPlan = async (
  webhookId: number,
  plan: LinkPlan,
  eventTypes: string[],
  options: WebhookEventTypeOption[]
) => {
  const AxiosUI = createApi("");
  const ids = eventTypeIds(eventTypes, options);
  for (const link of plan.remove) {
    await AxiosUI.delete(`webhook/${link.id}/`);
  }
  for (const link of plan.patch) {
    await AxiosUI.patch(`webhook/${link.id}/`, { event_types: ids });
  }
  if (plan.add.length > 0) {
    await AxiosUI.post(`webhook/add/`, {
      webhook: webhookId,
      streams: plan.add,
      custom_fields: {},
      event_types: ids,
    });
  }
};
